import React from 'react'
import { Link, useLocation } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import { cn } from '@/lib/utils'
import { MotionHighlight } from '@/components/animate-ui'
import { 
  LayoutDashboard, 
  FileText, 
  MapPin, 
  Newspaper, 
  FolderOpen,
  Image,
  Users,
  Star,
  Share2,
  Calendar,
  Settings,
  Zap,
  ShieldCheck,
  BarChart3,
  Camera,
  CheckSquare,
  MessageCircle,
  Building2,
  Database,
  Target
} from 'lucide-react'

interface SidebarProps {
  isCollapsed?: boolean
}

interface NavItem {
  title: string
  href: string
  icon: React.ComponentType<{ className?: string }>
  description?: string
  badge?: string
  children?: NavItem[]
}

const navigationItems: NavItem[] = [
  {
    title: 'Dashboard',
    href: '/',
    icon: LayoutDashboard,
    description: 'Overview and quick actions'
  },
  {
    title: 'Content Management',
    href: '/content',
    icon: FileText,
    description: 'Manage website content',
    children: [
      { title: 'Services', href: '/content/services', icon: Settings },
      { title: 'Locations', href: '/content/locations', icon: MapPin },
      { title: 'Blog Posts', href: '/content/blog', icon: Newspaper },
      { title: 'Static Pages', href: '/content/pages', icon: FileText },
      { title: 'Projects', href: '/content/projects', icon: Building2 }
    ]
  },
  {
    title: 'Media & Gallery',
    href: '/media',
    icon: Image,
    description: 'Gallery and asset management',
    children: [
      { title: 'Gallery Management', href: '/media/galleries', icon: Camera },
      { title: 'Repository', href: '/media/repository', icon: FolderOpen },
      { title: 'Approval Queue', href: '/media/approval', icon: CheckSquare, badge: '12' },
      { title: 'Asset Library', href: '/media/assets', icon: Database }
    ]
  },
  {
    title: 'Customer Operations',
    href: '/customers',
    icon: Users,
    description: 'Leads and customer management',
    children: [
      { title: 'Leads Management', href: '/customers/leads', icon: MessageCircle, badge: '5' },
      { title: 'Lead Attribution', href: '/customers/attribution', icon: Target },
      { title: 'Customer Data', href: '/customers/data', icon: Users }
    ]
  },
  {
    title: 'Marketing & Social',
    href: '/marketing',
    icon: Share2,
    description: 'Social media and campaigns',
    children: [
      { title: 'Social Media', href: '/marketing/social', icon: Share2 },
      { title: 'Content Calendar', href: '/marketing/calendar', icon: Calendar },
      { title: 'Campaign Tracking', href: '/marketing/campaigns', icon: BarChart3 },
      { title: 'Social Analytics', href: '/marketing/analytics', icon: BarChart3 }
    ]
  },
  {
    title: 'Integrations',
    href: '/integrations',
    icon: Zap,
    description: 'External service connections',
    children: [
      { title: 'Jobber CRM', href: '/integrations/jobber', icon: Building2 },
      { title: 'Google My Business', href: '/integrations/gmb', icon: MapPin, badge: '3' },
      { title: 'Analytics', href: '/integrations/analytics', icon: BarChart3 }
    ]
  },
  {
    title: 'Administration',
    href: '/admin',
    icon: ShieldCheck,
    description: 'System settings and security',
    children: [
      { title: 'User Management', href: '/admin/users', icon: Users },
      { title: 'Security & Audit', href: '/admin/security', icon: ShieldCheck },
      { title: 'Site Settings', href: '/admin/settings', icon: Settings }
    ]
  }
]

export default function Sidebar({ isCollapsed = false }: SidebarProps) {
  const location = useLocation()
  const [expandedSections, setExpandedSections] = React.useState<string[]>([])

  const toggleSection = (href: string) => {
    setExpandedSections(prev => 
      prev.includes(href) 
        ? prev.filter(item => item !== href)
        : [...prev, href]
    )
  }

  const isActive = (href: string) => {
    if (href === '/') {
      return location.pathname === '/'
    }
    return location.pathname.startsWith(href)
  }

  const isParentActive = (item: NavItem) => {
    if (item.children) {
      return item.children.some(child => isActive(child.href))
    }
    return isActive(item.href)
  }

  React.useEffect(() => {
    // Auto-expand active sections
    navigationItems.forEach(item => {
      if (item.children && isParentActive(item)) {
        setExpandedSections(prev => 
          prev.includes(item.href) ? prev : [...prev, item.href]
        )
      }
    })
  }, [location.pathname])

  return (
    <div className={cn(
      "flex flex-col h-full bg-card border-r transition-all duration-300",
      isCollapsed ? "w-16" : "w-64"
    )}>
      {/* Header */}
      <div className="p-4 border-b">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 bg-primary rounded-lg flex items-center justify-center">
            <span className="text-primary-foreground font-bold text-sm">FB</span>
          </div>
          {!isCollapsed && (
            <div>
              <h2 className="font-semibold text-sm">FixMyBrick</h2>
              <p className="text-xs text-muted-foreground">Admin Dashboard</p>
            </div>
          )}
        </div>
      </div>

      {/* Navigation */}
      <nav className="flex-1 p-4 space-y-2 overflow-y-auto">
        {navigationItems.map((item) => (
          <div key={item.href}>
            {/* Main Navigation Item */}
            <MotionHighlight
              isActive={isParentActive(item)}
              highlightClassName="bg-primary/10 border border-primary/20"
              className="rounded-lg"
            >
              <motion.div
                className={cn(
                  "flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-colors",
                  isParentActive(item)
                    ? "bg-primary text-primary-foreground"
                    : "hover:bg-accent hover:text-accent-foreground",
                  "cursor-pointer"
                )}
                onClick={() => {
                  if (item.children) {
                    toggleSection(item.href)
                  }
                }}
                whileHover={{ x: 2 }}
                whileTap={{ scale: 0.98 }}
                transition={{ duration: 0.2 }}
              >
                <motion.div
                  whileHover={{ rotate: 5, scale: 1.1 }}
                  transition={{ duration: 0.2 }}
                >
                  <item.icon className="w-4 h-4 flex-shrink-0" />
                </motion.div>
                {!isCollapsed && (
                  <>
                    <span className="flex-1">{item.title}</span>
                    {item.badge && (
                      <motion.span 
                        className="bg-destructive text-destructive-foreground text-xs px-1.5 py-0.5 rounded-full"
                        whileHover={{ scale: 1.1 }}
                        transition={{ duration: 0.2 }}
                      >
                        {item.badge}
                      </motion.span>
                    )}
                  </>
                )}
              </motion.div>
            </MotionHighlight>

            {/* Sub-navigation */}
            <AnimatePresence>
              {item.children && !isCollapsed && expandedSections.includes(item.href) && (
                <motion.div 
                  className="ml-4 mt-2 space-y-1"
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  transition={{ duration: 0.3, ease: 'easeInOut' }}
                >
                  {item.children.map((child, index) => (
                    <motion.div
                      key={child.href}
                      initial={{ opacity: 0, x: -10 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: -10 }}
                      transition={{ duration: 0.3, delay: index * 0.05 }}
                    >
                      <MotionHighlight
                        isActive={isActive(child.href)}
                        highlightClassName="bg-primary/5 border-l-2 border-primary/30"
                        className="rounded-lg"
                      >
                        <Link
                          to={child.href}
                          className={cn(
                            "flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-colors",
                            isActive(child.href)
                              ? "bg-primary/10 text-primary border-l-2 border-primary"
                              : "hover:bg-accent hover:text-accent-foreground"
                          )}
                        >
                          <motion.div
                            whileHover={{ rotate: 5, scale: 1.1 }}
                            transition={{ duration: 0.2 }}
                          >
                            <child.icon className="w-4 h-4 flex-shrink-0" />
                          </motion.div>
                          <span className="flex-1">{child.title}</span>
                          {child.badge && (
                            <motion.span 
                              className="bg-destructive text-destructive-foreground text-xs px-1.5 py-0.5 rounded-full"
                              whileHover={{ scale: 1.1 }}
                              transition={{ duration: 0.2 }}
                            >
                              {child.badge}
                            </motion.span>
                          )}
                        </Link>
                      </MotionHighlight>
                    </motion.div>
                  ))}
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        ))}
      </nav>

      {/* Footer */}
      {!isCollapsed && (
        <div className="p-4 border-t text-xs text-muted-foreground">
          <p>FixMyBrick Admin v1.0</p>
          <p>© 2025 FixMyBrick Inc.</p>
        </div>
      )}
    </div>
  )
}